import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { customToast, toastMessages } from '../utils/toast';
import { Eye, EyeOff, Mail, Lock, User, Building2, Sparkles, ArrowRight, CheckCircle, Users, CreditCard, AlertCircle } from 'lucide-react';
import api from '../services/api';
import EmailVerification from '../components/EmailVerification';
import StaffLimitWarning from '../components/StaffLimitWarning';

// Initialize Stripe
// Initialize Stripe with direct key fallback for production stability
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51SaNgM7qJJQapbT0reOGEe91CLK6qwoNORlPJw78DUscqKf7SLA7SUpW7hWmIof74viGSQnTU2xIv2iLRSV55Efm00roDfqrea';
const stripePromise = loadStripe(STRIPE_KEY);

// Card input styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,
};

// Main Register Component
const Register = () => {
  return (
    <Elements stripe={stripePromise}>
      <RegisterForm />
    </Elements>
  );
};

// Inner Form Component with Stripe hooks
const RegisterForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    type: 'new_restaurant', // 'new_restaurant' or 'join_restaurant'
    restaurantName: '',
    invitationCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [staffLimitReached, setStaffLimitReached] = useState(false);
  const [step, setStep] = useState('form'); // 'form' or 'verification'
  const [verificationEmail, setVerificationEmail] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');

  // Store payment data temporarily (only for owners)
  const [tempPaymentData, setTempPaymentData] = useState(null);

  const stripe = useStripe();
  const elements = useElements();
  const { register } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    const strength = calculatePasswordStrength(formData.password);
    setPasswordStrength(strength);
  }, [formData.password]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return Math.min(strength, 5);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : '');

    // Clear temp data when card changes
    if (tempPaymentData) {
      setTempPaymentData(null);
    }
  };

  // Create payment method ONLY for restaurant owners
  const createAndStorePaymentMethod = async () => {
    if (!stripe || !elements) {
      throw new Error('Stripe not initialized');
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      throw new Error('Card element not found');
    }

    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: formData.name,
        email: formData.email
      }
    });

    if (stripeError) {
      throw new Error(stripeError.message);
    }

    // Store payment method data temporarily
    setTempPaymentData({
      id: paymentMethod.id,
      card: {
        brand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4
      }
    });

    return paymentMethod.id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Basic validation for all users
    if (formData.password !== formData.confirmPassword) {
      customToast.error(toastMessages.passwordMismatch);
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      customToast.error(toastMessages.passwordTooShort);
      setLoading(false);
      return;
    }

    // Type-specific validation
    if (formData.type === 'new_restaurant') {
      // Restaurant owner: Must have valid card
      if (!cardComplete) {
        customToast.error(t('auth.register.completeCardDetails', 'Please complete all card details to start your free trial'));
        setLoading(false);
        return;
      }

      if (!formData.restaurantName) {
        customToast.error(t('auth.register.restaurantNameRequired', 'Restaurant name is required'));
        setLoading(false);
        return;
      }
    } else if (formData.type === 'join_restaurant') {
      // Staff member: Must have invitation code
      if (!formData.invitationCode) {
        customToast.error(t('auth.register.restaurantIdRequired', 'Restaurant ID is required'));
        setLoading(false);
        return;
      }

      if (staffLimitReached) {
        customToast.error(t('auth.register.staffLimitReachedMsg', 'This restaurant has reached its staff limit. Registration is blocked.'));
        setLoading(false);
        return;
      }
    }

    const loadingToast = customToast.loading(t('common.loading'));

    try {
      // Step 1: Send verification code to email
      const result = await api.post('/send-verification.php', {
        email: formData.email
      });

      if (result && result.success) {
        customToast.dismissSpecific(loadingToast);
        customToast.success(t('auth.register.verificationSent', 'Verification code sent to your email'));
        setVerificationEmail(formData.email);

        // Step 2: Only create payment method for RESTAURANT OWNERS
        if (formData.type === 'new_restaurant') {
          try {
            await createAndStorePaymentMethod();
            customToast.success(t('auth.register.cardValidated', 'Card validated successfully'));
          } catch (paymentError) {
            console.error('Payment method creation error:', paymentError);
            customToast.error(paymentError.message || t('auth.register.cardProcessFailed', 'Failed to process card. Please check your card details.'));
            setLoading(false);
            return;
          }
        }

        // Go to verification for both types
        setStep('verification');
      } else {
        customToast.dismissSpecific(loadingToast);
        const errorMessage = result?.message || t('auth.register.verificationFailed', 'Failed to send verification code');
        customToast.error(errorMessage);
        setLoading(false);
      }
    } catch (error) {
      console.error('Register.js - Verification error:', error);
      customToast.dismissSpecific(loadingToast);
      customToast.error(error.message || t('auth.register.verificationFailed', 'Failed to send verification code'));
      setLoading(false);
    }
  };

  // Handle verified email - different data for owners vs staff
  const handleVerified = async () => {
    setLoading(true);
    const loadingToast = customToast.loading(t('auth.register.creatingAccount', 'Creating your account...'));

    try {
      // Prepare base registration data
      const data = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        action: 'register_user',
        registrationType: formData.type
      };

      // Add type-specific data
      if (formData.type === 'new_restaurant') {
        // Restaurant owner: Include payment method for trial
        if (!tempPaymentData || !tempPaymentData.id) {
          throw new Error(t('auth.register.paymentLost', 'Payment information lost. Please go back and enter your card again.'));
        }
        data.restaurantName = formData.restaurantName;
        data.payment_method_id = tempPaymentData.id;
        data.is_trial = true;

      } else {
        // Staff member: Just invitation code, no payment
        data.invitationCode = formData.invitationCode;
        data.is_trial = false; // Staff don't get trials
      }

      console.log('Register.js - Sending registration:', data);

      // Register user
      const result = await register(data, 'user');
      console.log('Register.js - Registration result:', result);

      customToast.dismissSpecific(loadingToast);

      if (result && result.success) {
        // Different success messages based on user type
        if (formData.type === 'new_restaurant') {
          customToast.success(t('auth.register.restaurantCreated', 'Restaurant created! Your 30-day free trial has started.'));
          if (result.data?.trial_end) {
            customToast.success(t('auth.register.noChargeUntil', "Your card won't be charged until ") +
              new Date(result.data.trial_end).toLocaleDateString());
          }
        } else {
          customToast.success(t('auth.register.joinedRestaurant', 'You have successfully joined the restaurant!'));
        }

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard', {
            state: {
              newUser: true,
              userType: formData.type,
              trialStarted: formData.type === 'new_restaurant'
            }
          });
        }, 2000);
      } else {
        throw new Error(result?.message || result?.error || t('auth.register.registrationFailed', 'Registration failed'));
      }

    } catch (error) {
      console.error('Register.js - Registration error:', error);
      customToast.dismissSpecific(loadingToast);
      customToast.error(error.message || t('auth.register.registrationFailed', 'Registration failed'));
      setStep('form'); // Go back to form on error
    } finally {
      setLoading(false);
    }
  };

  // Go back to form from verification
  const handleBackToForm = () => {
    setStep('form');
    // Don't reset card state to preserve entered data
  };

  const passwordStrengthColors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-green-500'
  ];

  const passwordStrengthLabels = t('auth.register.passwordStrengths.labels', [
    'Very Weak',
    'Weak',
    'Fair',
    'Good',
    'Strong'
  ]);

  const safePasswordStrengthLabels = Array.isArray(passwordStrengthLabels) ? passwordStrengthLabels : [];

  const accountTypes = [
    {
      value: 'new_restaurant',
      label: t('auth.register.accountTypes.restaurant.label', 'Restaurant Owner'),
      icon: Building2,
      description: t('auth.register.accountTypes.restaurant.description', 'Create and manage your restaurant'),
      gradient: 'from-blue-500 to-cyan-500',
      badge: t('auth.register.accountTypes.restaurant.badge', '30-day free trial')
    },
    {
      value: 'join_restaurant',
      label: t('auth.register.accountTypes.staff.label', 'Staff Member'),
      icon: Users,
      description: t('auth.register.accountTypes.staff.description', 'Join existing restaurant'),
      gradient: 'from-purple-500 to-violet-500',
      badge: t('auth.register.accountTypes.staff.badge', 'invitation required')
    }
  ];

  const getRegisterBenefits = () => {
    return {
      owner: [
        t('auth.register.benefits.owner.0', "Set up in under 5 minutes"),
        t('auth.register.benefits.owner.1', "Enter card for 30-day free trial"),
        t('auth.register.benefits.owner.2', "No charge until trial ends"),
        t('auth.register.benefits.owner.3', "Cancel anytime during trial"),
        t('auth.register.benefits.owner.4', "24/7 customer support")
      ],
      staff: [
        t('auth.register.benefits.staff.0', "Join your restaurant team"),
        t('auth.register.benefits.staff.1', "No payment required"),
        t('auth.register.benefits.staff.2', "Access menu management"),
        t('auth.register.benefits.staff.3', "Process orders"),
        t('auth.register.benefits.staff.4', "Track statistics")
      ]
    };
  };

  const registerBenefits = getRegisterBenefits();
  const currentBenefits = formData.type === 'new_restaurant'
    ? registerBenefits.owner
    : registerBenefits.staff;

  // Show card status if we have temp data (only for owners)
  const getCardStatusText = () => {
    if (tempPaymentData) {
      return t('auth.register.cardReady', 'Card ready:') + ` ${tempPaymentData.card.brand} ` + t('auth.register.endingIn', 'ending in') + ` ${tempPaymentData.card.last4}`;
    }
    if (cardComplete) {
      return t('auth.register.cardValid', 'Card valid - No charge today');
    }
    return '';
  };

  return (
    <>
      {step === 'verification' ? (
        <EmailVerification
          email={verificationEmail}
          onVerified={handleVerified}
          onBack={handleBackToForm}
          loading={loading}
        />
      ) : (
        <div className="min-h-screen bg-white flex">
          {/* Left Side - Visual */}
          <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-green-600 via-emerald-600 to-cyan-600">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.3),transparent_50%)]"></div>

            <div className="relative z-10 flex flex-col justify-center px-12 text-white max-w-lg mx-auto">
              <div className={`transition-all duration-700 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-8">
                  <Sparkles className="h-4 w-4" />
                  {formData.type === 'new_restaurant'
                    ? t('auth.register.startTrialBadge', 'Start your 30-day free trial')
                    : t('auth.register.joinTeamBadge', 'Join your restaurant team')}
                </div>

                <h2 className="text-5xl font-bold mb-6 leading-tight">
                  {formData.type === 'new_restaurant' ? (
                    <>
                      {t('auth.register.visual.title.part1', 'Try risk-free for')}{' '}
                      <span className="text-cyan-200">
                        {t('auth.register.visual.title.part2', '30 days')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-cyan-200">{t('auth.register.visual.join', 'Join')}</span> {t('auth.register.visual.yourTeam', 'your team')}
                    </>
                  )}
                </h2>

                <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                  {formData.type === 'new_restaurant'
                    ? t('auth.register.ownerDesc', 'Enter your card details to start your free trial. No charge until day 31.')
                    : t('auth.register.staffDesc', 'Use your invitation code to join your restaurant team and start helping.')}
                </p>

                {/* Benefits List - Dynamic based on user type */}
                <div className="space-y-4 mb-8">
                  {currentBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-emerald-100">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <div className="text-2xl font-bold">10k+</div>
                    <div className="text-sm text-emerald-100">
                      {t('home.stats.restaurants', 'Restaurants')}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <div className="text-2xl font-bold">98%</div>
                    <div className="text-sm text-emerald-100">
                      {t('auth.register.visual.satisfaction', 'Satisfaction')}
                    </div>
                  </div>
                </div>

                {/* Trust Badge - Only for owners */}
                {formData.type === 'new_restaurant' && (
                  <div className="mt-8 flex items-center gap-2 text-sm text-emerald-100">
                    <CreditCard className="h-4 w-4" />
                    <span>{t('auth.register.securePayment', 'Secure payment - No charge today')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
            <div className={`max-w-md w-full space-y-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              {/* Header */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Sparkles className="h-4 w-4" />
                  {formData.type === 'new_restaurant'
                    ? t('auth.register.trialBadge', '30-Day Free Trial')
                    : t('auth.register.joinBadge', 'Join Restaurant Team')}
                </div>

                <h1 className="text-4xl font-bold text-gray-900 mb-3">
                  {formData.type === 'new_restaurant'
                    ? t('auth.register.title', 'Start Your Free Trial')
                    : t('auth.register.joinTitle', 'Join Your Restaurant')}
                </h1>
                <p className="text-lg text-gray-600">
                  {formData.type === 'new_restaurant'
                    ? t('auth.register.ownerSubtitle', 'Enter card details to begin - no charge today')
                    : t('auth.register.staffSubtitle', 'Enter your invitation code to join')}
                </p>
              </div>

              {/* Account Type Selection */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('auth.register.accountTypeLabel', 'I am a:')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {accountTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.type === type.value;

                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          type: type.value,
                          // Clear type-specific fields when switching
                          restaurantName: type.value === 'new_restaurant' ? formData.restaurantName : '',
                          invitationCode: type.value === 'join_restaurant' ? formData.invitationCode : ''
                        })}
                        className={`relative p-4 rounded-xl text-left transition-all duration-300 border-2 ${isSelected
                            ? `border-transparent bg-gradient-to-r ${type.gradient} text-white shadow-lg transform scale-105`
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                          }`}
                      >
                        {/* Badge for account type */}
                        <div className={`absolute -top-2 -right-2 text-xs px-2 py-1 rounded-full ${isSelected ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-600'
                          }`}>
                          {type.badge}
                        </div>

                        <div className={`flex items-center gap-3 mb-2 ${isSelected ? 'text-white' : 'text-gray-600'
                          }`}>
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/20' : 'bg-gray-100'
                            }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-semibold text-sm">{type.label}</span>
                        </div>
                        <p className={`text-xs ${isSelected ? 'text-white/90' : 'text-gray-500'
                          }`}>
                          {type.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Form */}
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-5">
                  {/* Personal Name Field */}
                  <div className="group">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.type === 'new_restaurant'
                        ? t('auth.register.ownerName', 'Owner Full Name')
                        : t('auth.register.fullName', 'Full Name')
                      }
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <User className={`h-5 w-5 transition-colors duration-300 ${formData.name ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                          }`} />
                      </div>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="block w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={formData.type === 'new_restaurant'
                          ? t('auth.register.ownerNamePlaceholder', 'Enter owner full name')
                          : t('auth.register.fullNamePlaceholder', 'Enter your full name')
                        }
                      />
                    </div>
                  </div>

                  {/* Conditional Fields based on user type */}
                  {formData.type === 'new_restaurant' ? (
                    // RESTAURANT OWNER FIELDS
                    <>
                      {/* Restaurant Name Field */}
                      <div className="group">
                        <label htmlFor="restaurantName" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('auth.register.restaurantName', 'Restaurant Name')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Building2 className={`h-5 w-5 transition-colors duration-300 ${formData.restaurantName ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                              }`} />
                          </div>
                          <input
                            id="restaurantName"
                            name="restaurantName"
                            type="text"
                            required
                            value={formData.restaurantName}
                            onChange={handleChange}
                            className="block w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                            placeholder={t('auth.register.restaurantNamePlaceholder', 'Enter restaurant name')}
                          />
                        </div>
                      </div>

                      {/* Card Element - Only for restaurant owners */}
                      <div className="group">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('auth.register.creditCard', 'Credit Card')} <span className="text-green-600 text-xs">({t('auth.register.noChargeToday', 'No charge today')})</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                            <CreditCard className={`h-5 w-5 transition-colors duration-300 ${cardComplete ? 'text-green-600' : 'text-gray-400'
                              }`} />
                          </div>
                          <div className="pl-12">
                            <CardElement
                              options={CARD_ELEMENT_OPTIONS}
                              onChange={handleCardChange}
                              className="block w-full bg-white border border-gray-200 rounded-xl py-4 px-4 focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent transition-all duration-300 hover:border-gray-300"
                            />
                          </div>
                        </div>

                        {cardError && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            <span>{cardError}</span>
                          </div>
                        )}

                        {getCardStatusText() && !cardError && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>{getCardStatusText()}</span>
                          </div>
                        )}
                      </div>

                      {/* Trial Notice - Only for owners */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CreditCard className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-blue-800 mb-1">
                              {t('auth.register.trialNoticeTitle', '30-Day Free Trial')}
                            </p>
                            <p className="text-xs text-blue-700">
                              {t('auth.register.trialNoticeDesc', "Your card won't be charged today. After 30 days, your subscription will begin automatically. Cancel anytime during your trial with no cost.")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // STAFF MEMBER FIELDS
                    <>
                      {/* Invitation Code Field */}
                      <div className="group">
                        <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('auth.register.invitationCode', 'Restaurant ID')}
                        </label>
                        <input
                          id="invitationCode"
                          name="invitationCode"
                          type="text"
                          required
                          value={formData.invitationCode}
                          onChange={handleChange}
                          className="block w-full px-4 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                          placeholder={t('auth.register.invitationCodePlaceholder', 'Enter restaurant ID provided by your manager')}
                        />
                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {t('auth.register.askManager', 'Ask your restaurant manager for the Restaurant ID')}
                        </p>

                        {/* Real-time limit check */}
                        {formData.invitationCode && formData.invitationCode.length > 0 && (
                          <StaffLimitWarning
                            restaurantId={formData.invitationCode}
                            onLimitReached={setStaffLimitReached}
                          />
                        )}
                      </div>

                      {/* Staff Info Message */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Users className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-purple-800 mb-1">
                              {t('auth.register.joiningStaffTitle', 'Joining as Staff Member')}
                            </p>
                            <p className="text-xs text-purple-700">
                              {t('auth.register.joiningStaffDesc', "You're joining an existing restaurant. No payment required. The restaurant owner manages the subscription.")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Email Field - Common for both */}
                  <div className="group">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.emailAddress', 'Email address')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className={`h-5 w-5 transition-colors duration-300 ${formData.email ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                          }`} />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="block w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={t('auth.enterEmail', 'Enter your email address')}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="group">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.password', 'Password')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className={`h-5 w-5 transition-colors duration-300 ${formData.password ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                          }`} />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="block w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={t('auth.register.passwordPlaceholder', 'Create a strong password')}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors duration-300 hover:text-green-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Meter */}
                    {formData.password && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">
                            {t('auth.register.passwordStrength', 'Password strength:')}
                          </span>
                          <span className={`font-medium ${passwordStrength >= 4 ? 'text-green-600' :
                              passwordStrength >= 3 ? 'text-blue-600' :
                                passwordStrength >= 2 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                            {safePasswordStrengthLabels[passwordStrength - 1] || safePasswordStrengthLabels[0] || 'Very Weak'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((index) => (
                            <div
                              key={index}
                              className={`h-1 flex-1 rounded-full transition-all duration-300 ${index <= passwordStrength
                                  ? passwordStrengthColors[passwordStrength - 1]
                                  : 'bg-gray-200'
                                }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="group">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.register.confirmPassword', 'Confirm Password')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className={`h-5 w-5 transition-colors duration-300 ${formData.confirmPassword ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                          }`} />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="block w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={t('auth.register.confirmPasswordPlaceholder', 'Confirm your password')}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors duration-300 hover:text-green-600"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {/* Password Match Indicator */}
                    {formData.confirmPassword && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        {formData.password === formData.confirmPassword ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-600">
                              {t('auth.register.passwordsMatch', 'Passwords match')}
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                            </div>
                            <span className="text-red-600">
                              {t('auth.register.passwordsNoMatch', 'Passwords do not match')}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms Agreement - Common for both */}
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    required
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span>
                    {t('auth.register.terms.part1', 'I agree to the')}{' '}
                    <Link to="/terms" className="text-green-600 hover:text-green-500 font-medium">
                      {t('auth.register.terms.terms', 'Terms of Service')}
                    </Link>{' '}
                    {t('auth.register.terms.part2', 'and')}{' '}
                    <Link to="/privacy" className="text-green-600 hover:text-green-500 font-medium">
                      {t('auth.register.terms.privacy', 'Privacy Policy')}
                    </Link>
                  </span>
                </div>

                {/* Submit Button - Dynamic text based on user type */}
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !stripe ||
                    (formData.type === 'new_restaurant' && !cardComplete) ||
                    (formData.type === 'join_restaurant' && staffLimitReached)
                  }
                  className="group relative w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                      {t('auth.register.creatingAccount', 'Creating your account...')}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      {formData.type === 'new_restaurant'
                        ? t('auth.register.startTrialBtn', 'Start 30-Day Free Trial')
                        : (staffLimitReached ? t('auth.register.staffLimitReachedBtn', 'Staff Limit Reached') : t('auth.register.joinRestaurantBtn', 'Join Restaurant'))}
                      {!staffLimitReached && (
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      )}
                    </div>
                  )}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">
                      {t('auth.register.alreadyHaveAccount', 'Already have an account?')}
                    </span>
                  </div>
                </div>

                {/* Sign In Link */}
                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center w-full border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold text-lg hover:border-green-600 hover:text-green-600 transition-all duration-300 hover:scale-105"
                  >
                    {t('auth.register.signInLink', 'Sign in to your account')}
                  </Link>
                </div>

                {/* Security Note - Only for owners */}
                {formData.type === 'new_restaurant' && (
                  <p className="text-xs text-center text-gray-500">
                    🔒 {t('auth.register.securePaymentNote', 'Your payment information is securely processed by Stripe. We never store your full card details.')}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;